import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DEMO_YES_NO_OPTIONS } from '../../core/config/demo-validation.config';
import { ArrayRadioButton, HybridModel } from '../../shared/models/hybrid-model/hybrid-model';
import { CardInfoModel } from '../../shared/components-generals/card-info/card-info';
import { FileCaptureCardModel } from '../../shared/components-generals/card-file-capture/card-file-capture';

// Card de navegación hacia un formulario dinámico (sin evidencia asociada)
export interface FormNavigationCardModel extends CardInfoModel {
  key: string;
}

const ASSET_TEXT_PATTERN = '[a-zA-Z0-9áéíóúÁÉÍÓÚÑñ\\ \\.\\@_\\-#$%&\\/,:;\\(\\)\\+]{0,255}';
const ASSET_LONG_TEXT_PATTERN = '[a-zA-Z0-9áéíóúÁÉÍÓÚÑñ\\ \\.\\@_\\-#$%&\\/,:;\\(\\)\\+]{0,800}';

const FORM_KEYS = {
  locations: {
    user: '142',
    form: '143',
    evidences: '144',
    vpn: '145',
  },
  asset: {
    conditions: {
      odometerReading: '148',
      hasDamage: '149',
      hasFaultOrWarning: '150',
      hasPartsChanged: '151',
      hasModification: '152',
    },
    maintenance: {
      lastService: '153',
      nextService: '154',
      comments: '155',
      serviceReceipt: '156',
    },
    insurance: {
      policyActive: '157',
      hasClaim: '158',
      permitsUpToDate: '159',
      policyFile: '160',
      permitFile: '161',
    },
    evidence: {
      generalPhoto: '162',
      serialNumber: '163',
      odometerPhoto: '164',
      front: '165',
      rightSide: '166',
      leftSide: '167',
      rear: '168',
    },
  },
} as const;

const mapCardOptionsToDescriptionValue = (
  options: ReadonlyArray<ArrayRadioButton>,
): ArrayRadioButton[] => options.map((option) => ({ ...option, value: option.description }));

const YES_NO_CARD_OPTIONS = mapCardOptionsToDescriptionValue(DEMO_YES_NO_OPTIONS);

const cloneRadioOptions = (options: ReadonlyArray<ArrayRadioButton>): ArrayRadioButton[] =>
  options.map((option) => ({ ...option }));

// Condiciones generales del activo (ClosingFieldCategoryId 71)
const ASSET_CONDITIONS_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [Validators.required, Validators.pattern(ASSET_TEXT_PATTERN)]),
    key: FORM_KEYS.asset.conditions.odometerReading,
    label: '¿Cuál es la lectura actual del odómetro, horómetro o contador?',
    placeholder: 'Ej. 45,320',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.conditions.hasDamage,
    label: '¿Ha sufrido algún golpe, accidente o daño desde la última revisión?',
    placeholder: '¿Ha sufrido algún golpe, accidente o daño desde la última revisión?',
    type: 'input-general-cards',
    inputSelectRadioButton: { value: 'value', description: 'description' },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.conditions.hasFaultOrWarning,
    label: '¿Tiene alguna falla, ruido o testigo encendido actualmente?',
    placeholder: '¿Tiene alguna falla, ruido o testigo encendido actualmente?',
    type: 'input-general-cards',
    inputSelectRadioButton: { value: 'value', description: 'description' },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.conditions.hasPartsChanged,
    label: '¿Se le ha retirado, cambiado o agregado alguna pieza o accesorio?',
    placeholder: '¿Se le ha retirado, cambiado o agregado alguna pieza o accesorio?',
    type: 'input-general-cards',
    inputSelectRadioButton: { value: 'value', description: 'description' },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.conditions.hasModification,
    label: '¿Ha tenido alguna modificación (pintura, adaptaciones, cambio de componentes)?',
    placeholder: '¿Ha tenido alguna modificación (pintura, adaptaciones, cambio de componentes)?',
    type: 'input-general-cards',
    inputSelectRadioButton: { value: 'value', description: 'description' },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
];

// Campos de texto de Servicio (categoría 68) y Seguro (categoría 69); los archivos van en MAINTENANCE_FILE_CARDS
const MAINTENANCE_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.maintenance.lastService,
    label: '¿Cuándo fue el último servicio?',
    placeholder: 'Ej. 15/09/2025',
    type: 'input-general-calendar',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.maintenance.nextService,
    label: '¿Cuándo toca el próximo servicio?',
    placeholder: 'Ej. 15/12/2025',
    type: 'input-general-calendar',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(ASSET_LONG_TEXT_PATTERN),
    ]),
    key: FORM_KEYS.asset.maintenance.comments,
    label: 'Comentario adicional',
    placeholder: 'Comentario adicional',
    type: 'input-general-text-area',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.asset.insurance.policyActive,
    label: '¿La póliza de seguro está vigente?',
    placeholder: '¿La póliza de seguro está vigente?',
    type: 'input-general-cards',
    inputSelectRadioButton: { value: 'value', description: 'description' },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required, Validators.pattern(ASSET_TEXT_PATTERN)]),
    key: FORM_KEYS.asset.insurance.hasClaim,
    label: '¿Ha presentado algún reclamo al seguro?',
    placeholder: '¿Ha presentado algún reclamo al seguro?',
    type: 'input-general-text-area',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(ASSET_LONG_TEXT_PATTERN),
    ]),
    key: FORM_KEYS.asset.insurance.permitsUpToDate,
    label: '¿Los permisos, verificaciones o licencias están al día?',
    placeholder: '¿Los permisos, verificaciones o licencias están al día?',
    type: 'input-general-text-area',
    value: '',
    colFull: true,
    labelTop: true,
  },
];

// Cards de evidencia en PDF de Mantenimiento (código 156) y Seguro (códigos 160, 161)
const MAINTENANCE_FILE_CARDS: FileCaptureCardModel[] = [
  {
    key: 'serviceReceipt',
    code: FORM_KEYS.asset.maintenance.serviceReceipt,
    accept: 'application/pdf',
    titule: 'Comprobante de mantenimiento',
    text: '',
    icon: 'upload_file',
    colorIcon: '#c76e06',
    bg: '#fbd9b7',
    isButton: true,
    value: false,
  },
  {
    key: 'policyFile',
    code: FORM_KEYS.asset.insurance.policyFile,
    accept: 'application/pdf',
    titule: 'Poliza de seguro',
    text: '',
    icon: 'upload_file',
    colorIcon: '#c76e06',
    bg: '#fbd9b7',
    isButton: true,
    value: false,
  },
  {
    key: 'permitFile',
    code: FORM_KEYS.asset.insurance.permitFile,
    accept: 'application/pdf',
    titule: 'Permiso o licencia',
    text: '',
    icon: 'upload_file',
    colorIcon: '#c76e06',
    bg: '#fbd9b7',
    isButton: true,
    value: false,
  },
];

// Cards de evidencia fotográfica del activo (categoría 70, códigos 162-168); aceptan foto o PDF
const ASSET_EVIDENCE_FILE_CARDS: FileCaptureCardModel[] = [
  {
    key: 'generalPhoto',
    code: FORM_KEYS.asset.evidence.generalPhoto,
    accept: 'image/*,application/pdf',
    titule: 'Foto general del activo',
    text: '',
    icon: 'screenshot_frame',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
    isButton: true,
    value: false,
  },
  {
    key: 'serialNumber',
    code: FORM_KEYS.asset.evidence.serialNumber,
    accept: 'image/*,application/pdf',
    titule: 'Número de serie',
    text: '',
    icon: '123',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: true,
    value: false,
  },
  {
    key: 'odometerPhoto',
    code: FORM_KEYS.asset.evidence.odometerPhoto,
    accept: 'image/*,application/pdf',
    titule: 'Odómetro, horómetro o contador',
    text: '',
    icon: 'track_changes',
    colorIcon: '#009fa5',
    bg: '#c4e4e4',
    isButton: true,
    value: false,
  },
  {
    key: 'front',
    code: FORM_KEYS.asset.evidence.front,
    accept: 'image/*,application/pdf',
    titule: 'Frontal',
    text: '',
    icon: 'gamepad',
    colorIcon: '#c76e06',
    bg: '#fbd9b7',
    isButton: true,
    value: false,
  },
  {
    key: 'rightSide',
    code: FORM_KEYS.asset.evidence.rightSide,
    accept: 'image/*,application/pdf',
    titule: 'Lateral derecho',
    text: '',
    icon: 'gamepad_right',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
    isButton: true,
    value: false,
  },
  {
    key: 'leftSide',
    code: FORM_KEYS.asset.evidence.leftSide,
    accept: 'image/*,application/pdf',
    titule: 'Lateral izquierdo',
    text: '',
    icon: 'gamepad_left',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: true,
    value: false,
  },
  {
    key: 'rear',
    code: FORM_KEYS.asset.evidence.rear,
    accept: 'image/*,application/pdf',
    titule: 'Trasera',
    text: '',
    icon: 'gamepad_up',
    colorIcon: '#009fa5',
    bg: '#c4e4e4',
    isButton: true,
    value: false,
  },
];

// Todos los HybridModel mutables del flujo adout-user; se usan para capturar/restaurar su estado inicial.
const ALL_ADOUT_USER_FORM_MODELS: HybridModel[] = [
  ...ASSET_CONDITIONS_FORM_MODELS,
  ...MAINTENANCE_FORM_MODELS,
];

const ADOUT_USER_MAIN_CARDS: FormNavigationCardModel[] = [
  {
    key: 'assetConditions',
    titule: 'Condiciones del activo',
    text: '',
    icon: 'feature_search',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
    isButton: true,
    value: false,
  },
  {
    key: 'maintenance',
    titule: 'Mantenimiento',
    text: '',
    icon: 'settings',
    colorIcon: '#c76e06',
    bg: '#fbd9b7',
    isButton: true,
    value: false,
  },
  {
    key: 'assetEvidence',
    titule: 'Evidencia del equipo',
    text: '',
    icon: 'photo_camera',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: true,
    value: false,
  },
];

const cloneCards = <T>(cards: readonly T[]): T[] => cards.map((card) => ({ ...card }));

@Injectable({
  providedIn: 'root',
})
export class AdoutUserFormsService {
  public readonly formKeys = FORM_KEYS;

  public getAdoutUserMainCards(): FormNavigationCardModel[] {
    return cloneCards(ADOUT_USER_MAIN_CARDS);
  }

  public getAssetConditionsForm(): HybridModel[] {
    return ASSET_CONDITIONS_FORM_MODELS;
  }

  public getMaintenanceForm(): HybridModel[] {
    return MAINTENANCE_FORM_MODELS;
  }

  public getMaintenanceFileCards(): FileCaptureCardModel[] {
    return cloneCards(MAINTENANCE_FILE_CARDS);
  }

  public getAssetEvidenceFileCards(): FileCaptureCardModel[] {
    return cloneCards(ASSET_EVIDENCE_FILE_CARDS);
  }

  /** Restaura los HybridModel de adout-user a su estado inicial (equivalente a recargar la app). */
  public resetFormsState(): void {
    for (const model of ALL_ADOUT_USER_FORM_MODELS) {
      model.control.reset();
    }
  }
}
